<template>

  <mdb-container v-if="currentGroup">
    <banner-small :group="currentGroup" title="Basics"/>
    <mdb-card v-if="config.newGroupApproval && settings && settings.approvalStatus == 'NOT_SUBMITTED'"
              class="mb-4 text-left">
      <mdb-card-header class="warning-color text-white">
        <h5>Approval Required</h5>
      </mdb-card-header>
      <mdb-card-body>
        <p>Before your group can be listed publicly on {{ config.siteName }}, it must be approved. Before submitting it
          for approval, however, be sure that
          your group summary is accurate and there is enough information on your home page for {{ config.siteName }}
          admins to get a feel
          for what your group is about.
        </p>
        <mdb-btn size="sm" rounded @click="submitApproval" icon="check">Submit for Approval</mdb-btn>
      </mdb-card-body>
    </mdb-card>

    <mdb-card v-if="config.newGroupApproval && settings && settings.approvalStatus == 'SUBMITTED'"
              class="mb-4 text-left">
      <mdb-card-header class="success-color text-white">
        <h5>Approval Required</h5>
      </mdb-card-header>
      <mdb-card-body>
        <p>Your group has been submitted and is being reviewed.</p>
      </mdb-card-body>
    </mdb-card>

    <mdb-card v-if="config.newGroupApproval && settings && settings.approvalStatus == 'DENIED'" class="mb-4 text-left">
      <mdb-card-header class="danger-color text-white">
        <h5>Not Approved</h5>
      </mdb-card-header>
      <mdb-card-body>
        <p>We're sorry, but your group has been denied for public listing by the administrator.
          Please review your group for completeness and conformance to the terms of service before
          re-submitting for approval.
        </p>
      </mdb-card-body>
    </mdb-card>

    <mdb-card>
      <mdb-card-body>

        <form @keypress.13.prevent @submit.prevent="doSave" class="text-left needs-validation" v-if="settings">
          <mdb-row>
            <mdb-col col="6">
              <mdb-input @blur="tips.name=false" @focus="tips.name=true" label="Group Name"
                         placeholder="What is your group called?" required type="text"
                         v-model="settings.name"
              />
              <div class="form-text" v-if="tips.name">
                <mdb-icon icon="info-circle"/>
                Enter the name of your group as it will appear to your members and the general public (if your group is
                not
                private).
              </div>
            </mdb-col>

            <mdb-col col="6">
              <mdb-input @blur="tips.label=false" @focus="tips.label=true"
                         label="Member Label" placeholder="What are your members called?" type="text"
                         v-model="settings.memberLabel"
              />
              <div class="form-text" v-if="tips.label">
                <mdb-icon icon="info-circle"/>
                Enter a label for the members of your group. For example, if you are a biking group, a good label would
                be
                'bikers'.
              </div>
            </mdb-col>
          </mdb-row>
          <mdb-row>
            <mdb-col col="12">
              <mdb-input :rows="1" @blur="tips.summary=false" @focus="tips.summary=true" label="Summary"
                         placeholder="Summary" required type="textarea"
                         v-model="settings.groupSummary"
              />
              <div class="form-text" v-if="tips.summary">
                <mdb-icon icon="info-circle"/>
                Enter a brief summary of what your group is about. Seriously, keep it brief.
              </div>
            </mdb-col>
          </mdb-row>
          <mdb-row v-if="config.useGroupTypeLabels && groupTypes.length > 0">
            <mdb-col col="12">
              <gp-select
                :dataObj="settings" :possible-values="groupTypes" :required="true" label="Group Type"
                propertyName="groupType"/>
            </mdb-col>
          </mdb-row>

          <div> &nbsp;</div>
          <mdb-row>
            <mdb-col col="12">
              <div style="font-size: .9rem; color: #757575;">Categories</div>
              <cust-chip-input :chips="categoryTags" id="chip-input"/>
            </mdb-col>
          </mdb-row>

          <mdb-row>
            <mdb-col col="6">
              <mdb-input @blur="tips.vanityPath=false" @focus="tips.vanityPath=true"
                         label="URL Name" placeholder="URL Name" type="text"
                         v-model="settings.vanityPath"
              />
              <div class="form-text" v-if="tips.vanityPath">
                <mdb-icon icon="info-circle"/>
                Enter a custom name that will show in URLs for your group.
                For example, if your group is Happy Bikers of Burlington, you might try happy-bikers. Your URL would
                be
                {{ config.frontEndUrl }}/group/happy-bikers. You can use a-z, 0-9 and a dash.
              </div>

            </mdb-col>
            <mdb-col col="6">
              <gp-select :dataObj="settings" :possible-values="countries" :required="true" label="Country"
                         propertyName="country"/>
            </mdb-col>
          </mdb-row>
          <mdb-row>
            <mdb-col col="6">
              <mdb-input label="City" placeholder="City"
                         type="text"
                         v-model="settings.city"
              />
            </mdb-col>
            <mdb-col col="6">
              <mdb-input label="Postal" placeholder="Postal/Zip Code"
                         type="text"
                         v-model="settings.postal"
              />
            </mdb-col>
          </mdb-row>

          <mdb-row>
            <mdb-col col="6">
              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">When checked, this group will not be publicly visible. Only people with a secret
                    URL will be able to find it.
                  </span>
                  <span v-if="config.newGroupApproval && settings.approvalStatus != 'APPROVED'">
                    You will be able to toggle this setting if your group is approved. Until then, your
                    group will only be private.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Private Group?" class="form-control form-check-input" id="isPrivate"
                       type="checkbox"
                       :disabled="config.newGroupApproval && settings.approvalStatus != 'APPROVED'"
                       v-model="settings.isPrivate"/>
                <label for="isPrivate">Private group </label>
              </div>
              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">When checked, members can request to join, but will not become active members
                    until approved by a group administrator.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Require Approval?" class="form-control form-check-input" id="requireApproval"
                       type="checkbox" v-model="settings.requireApproval"/>
                <label for="requireApproval">Require approval to join </label>
              </div>
              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">When checked, members can create event suggestions that other members may comment
                    upon.
                    Admins and group organizers can convert suggestions into actual events.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Allow event suggestions?" class="form-control form-check-input"
                       id="allowSuggestions"
                       type="checkbox" v-model="settings.allowSuggestions"/>
                <label for="allowSuggestions">Allow event suggestions</label>
              </div>
              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">When checked, members will have access to an online page showing
                    their membership in your group. The ID will have a QR code that can be scanned
                    and verified to prove membership.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Enable ID card?" class="form-control form-check-input"
                       id="enableIdCard"
                       type="checkbox" v-model="settings.enableIdCard"/>
                <label for="enableIdCard">Enable ID card</label>
              </div>
            </mdb-col>
            <mdb-col col="6">
              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">When checked, potential members must have a photo/avatar associated with their
                    user profile before they are allowed to request membership to your group.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Require Avatar?" class="form-control form-check-input" id="requirePhoto"
                       type="checkbox"
                       v-model="settings.requirePhoto"/>
                <label for="requirePhoto">Require Profile Pic/Avatar </label>
              </div>

              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">If you use the accounting features of {{ config.siteName }}, you may check this box
                    if you
                    wish your finances to be transparent to group members.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Share Finances?" class="form-control form-check-input" id="shareFinances"
                       type="checkbox"
                       v-model="settings.shareFinances"/>
                <label for="shareFinances">Share finances </label>
              </div>

              <div class="form-check">
                <mdb-tooltip :options="{placement: 'left'}" trigger="hover">
                  <span slot="tip">If you have Stripe payments enabled, and this flag turned on, your
                    {{ $tc("common.label.member", 2) }}
                    will be able to make a donation to your {{ $tc("common.label.group", 1) }} from their account
                    page.
                  </span>
                  <mdb-icon icon="info-circle" slot="reference"/>
                </mdb-tooltip>
                <input aria-label="Accept Donations?" class="form-control form-check-input" id="donationsEnabled"
                       type="checkbox"
                       v-model="settings.donationsEnabled"/>
                <label for="donationsEnabled">Accept donations </label>
              </div>

            </mdb-col>
          </mdb-row>

          <mdb-row>
            <hr>
            <mdb-col col="12">
              <h5>Your group's URL</h5>
              <p><a :href="groupUrl">{{ groupUrl }}</a></p>
              <p v-if="settings.isPrivate">Because your group is private, only people with this secret URL will be
                able
                to find your group.</p>
            </mdb-col>
          </mdb-row>
          <hr>
          <mdb-row v-if="currentGroup.campfireActive">
            <mdb-col col="12">
              <gp-select
                :dataObj="settings" :possible-values="campfireStarters" :required="true"
                label="Who can start a Campfire?"
                propertyName="campfireStarter"/>
            </mdb-col>
          </mdb-row>
          <mdb-row>
            <mdb-col col="6">
              <mdb-input @blur="tips.eventMax=false" @focus="tips.eventMax=true"
                         label="Default Event Max" placeholder="Default max# people per event." type="number"
                         v-model="settings.defaultMaxAttend"
              />
              <div class="form-text" v-if="tips.eventMax">
                <mdb-icon icon="info-circle"/>
                When creating a new event, this will be the
                default max# of people allowed (host + member + guests). This can always be overridden on each event.
              </div>
            </mdb-col>
            <mdb-col col="6">
              <mdb-input @blur="tips.guestsMax=false" @focus="tips.guestsMax=true"
                         label="Default Max Guests" placeholder="Default max# guest for events" type="number"
                         v-model="settings.defaultNumGuests"
              />
              <div class="form-text" v-if="tips.guestsMax">
                <mdb-icon icon="info-circle"/>
                When creating a new event, this will be
                the default max# of guests allowed. This can always be overridden on each event.
              </div>
            </mdb-col>

          </mdb-row>

          <mdb-row>

            <mdb-col col="6">
              <gp-select
                :dataObj="settings" :possible-values="waitlistRules" :required="true" label="Default Waitlist Rule"
                propertyName="defaultWaitlistRule"/>
            </mdb-col>

            <mdb-col col="6">
              <mdb-input @blur="tips.messagingRule=false" @focus="tips.messagingRule=true"
                         label="Attendance Count Before Private Messaging Allowed" placeholder="# of Completed Events"
                         type="number"
                         v-model="settings.blockMessagingBeforeAttending"
              />
              <div class="form-text" v-if="tips.messagingRule">
                <mdb-icon icon="info-circle"/>
                When a new member joins, they must complete attendance in this number of events before they may
                <i>initiate</i> private messages with other members. They can always respond to messages they receive
                and
                may always message group admins. Admins may also override this rule on a member-by-member basis.
              </div>
            </mdb-col>

          </mdb-row>

          <mdb-btn icon="save" type="Submit">Save</mdb-btn>
        </form>
      </mdb-card-body>
    </mdb-card>
  </mdb-container>

</template>
<script>
import api from '../../services/groupsPlaceApi.js'
import bannerSmall from '../group/BannerSmall'
import gpSelect from '../common/GPSelect'
import iso from 'iso-3166-1'
import {custChipInput} from "../common/CustChipInput";
import utils from "../../services/utils";

import {
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardHeader,
  mdbCol,
  mdbContainer,
  mdbIcon,
  mdbInput,
  mdbRow,
  mdbTextarea,
  mdbTooltip
} from 'mdbvue';

export default {
  name: "group-basics",
  display: "Group Basics",
  order: 1,
  components: {
    mdbContainer,
    mdbBtn,
    mdbIcon,
    mdbTooltip,
    mdbInput,
    mdbRow,
    mdbCol,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbTextarea,
    bannerSmall,
    gpSelect,
    custChipInput
  },
  computed: {
    currentGroup: function () {
      return this.$store.state.currentGroup
    },
    userDetails: function () {
      return this.$store.state.userDetails
    },
    config: function () {
      return this.$store.state.settings
    },
    groupUrl: function () {
      let url = this.settings.baseUrl + "/group/"
      if (this.settings.vanityPath) url += this.settings.vanityPath
      else url += this.settings.id
      if (this.settings.isPrivate) url += "?secret=" + this.settings.accessToken
      return url
    }
  },

  data() {
    return {
      settings: null,
      categoryTags: [],
      countries: [],
      groupTypes: [],
      campfireStarters: [
        {text: "Admins", value: "ADMIN"}
      ],
      waitlistRules: [
        {text: "Manual", value: "Manual"},
        {text: "By RSVP Date", value: "By RSVP Date"},
        {text: "By Seniority", value: "By Seniority"}
      ],
      tips: {
        name: false, label: false, summary: false, categories: false,
        eventMax: false, guestsMax: false, vanityPath: false,
        messagingRule: false
      }
    }
  },
  beforeMount() {
    for (let i = 0; i < iso.all().length; i++) {
      let row = iso.all()[i]
      let c = {text: row.country, value: row.alpha3.toLowerCase()}
      this.countries.push(c)
    }
    if (this.userDetails.isAdmin || this.userDetails.proxied) this.countries.unshift({text: "Global", value: "GBL"})

  },
  async mounted() {
    document.title = "Group Basics";
    if (process.env.NEW_GROUP_APPROVAL == "yes") this.newGroupApproval = true;
    await this.$store.dispatch("setExecuting", true)
    await api.groupBasics(this.$route.params.id, "admin")
    if (!this.currentGroup) {
      await this.$store.dispatch("setExecuting", false)
      this.$notify.error({message: "Could not access group " + this.$route.params.id, timeOut: 10000})
      return
    }
    api.get("/group/basicSettings/" + this.$route.params.id).then(
      (details) => {
        this.settings = details.data
        if (this.settings.categories && this.settings.categories.startsWith(",")) {
          this.settings.categories = this.settings.categories.substr(1) //old frontend put a null tag in the beginning.
        }
        if (this.settings.country == "GBL" && !this.userDetails.isAdmin && !this.userDetails.proxied) {
          this.countries.unshift({text: "Global", value: "GBL"})
        }
        if (this.settings.reputationEnabled) {
          this.waitlistRules.push({text: this.settings.reputationLabel + " Score", value: "By Reputation"})
        }
        if (this.settings.categories) {
          this.categoryTags = this.settings.categories.split(",").sort()
        } else this.categoryTags = []
        if (this.categoryTags.length == 1 && this.categoryTags[0] == "") this.categoryTags = []

        this.campfireStarters.push({
                                     text: utils.capitalize(this.$tc("common.label.group", 1)) + " Organizers",
                                     value: "GROUP_ORGANIZER"
                                   })
        this.campfireStarters.push({
                                     text: utils.capitalize(this.$tc("common.label.event", 1)) + " Organizers",
                                     value: "EVENT_ORGANIZER"
                                   })
        this.campfireStarters.push({
                                     text: "Anyone in " + utils.capitalize(this.$tc("common.label.group", 1)),
                                     value: "MEMBER"
                                   })

        if (this.config.useGroupTypeLabels) {
          for (let type of this.config.groupTypes) {
            if (type.active) {
              let t = {text: type.name, value: type.id}
              this.groupTypes.push(t)
            }
          }
        }

      },
      (error) => {
        this.$notify.error({message: "Error getting settings."})
      }).then(() => {
      this.$store.dispatch("setExecuting", false)
    })
  },
  methods: {
    doSave: function () {
      if (!this.settings.country) {
        this.$notify.error({message: "Country is required."})
        return
      }
      this.$store.dispatch("setExecuting", true)
      this.settings.categories = this.categoryTags.join()
      api.post("/group/saveSettings/" + this.currentGroup.vanityPath, this.settings).then(
        (details) => {
          this.$notify.success({message: "Settings saved successfully."})
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    submitApproval: function () {
      this.$store.dispatch("setExecuting", true)
      api.post("/group/submitApproval/" + this.currentGroup.vanityPath).then(
        (details) => {
          this.$notify.success({message: "Your request has been submitted."});
          this.settings.approvalStatus = "SUBMITTED";
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 20000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    }
  }

};
</script>

<style scoped>

.tips-text {
  color: #757575;


}

</style>
